import { useMemo, useState } from "react";
import * as XLSX from "xlsx";

function LogsView({ activityLogs = [], themeMode = "light" }) {
  const isDark = themeMode === "dark";
  const palette = {
    pageText: isDark ? "#e5edf6" : "#0f172a",
    muted: isDark ? "#94a3b8" : "#64748b",
    card: isDark ? "#162235" : "#fff",
    card2: isDark ? "#101827" : "#f8fafc",
    border: isDark ? "#27364d" : "#e2e8f0",
    borderSoft: isDark ? "#2c3d55" : "#d1d5db",
    input: isDark ? "#0f172a" : "#fff",
    detail: isDark ? "#cbd5e1" : "#334155",
    primary: isDark ? "#67e8f9" : "#124254"
  };

  const getActionLabel = (action) => {
    const labels = {
      PROJE_EKLENDI: "Proje Eklendi",
      PROJE_GUNCELLENDI: "Proje Güncellendi",
      PROJE_SILINDI: "Proje Silindi",
      DOSYA_YUKLENDI: "Dosya Yüklendi",
      DOSYA_SILINDI: "Dosya Silindi",
      EXCEL_YUKLENDI: "Excel Yüklendi",
      KULLANICI_OLUSTURULDU: "Kullanıcı Oluşturuldu",
      KULLANICI_SILINDI: "Kullanıcı Silindi",
      SUREC_PROJESI_EKLENDI: "Süreç Projesi Eklendi",
      SUREC_PROJESI_GUNCELLENDI: "Süreç Projesi Güncellendi",
      SUREC_PROJESI_PASIFLESTIRILDI: "Süreç Projesi Pasifleştirildi",
      SUREC_PROJESI_SIRASI_GUNCELLENDI: "Süreç Projesi Sırası Güncellendi",
      SUREC_SABLONU_EKLENDI: "Süreç Şablonu Eklendi",
      SUREC_SABLONU_GUNCELLENDI: "Süreç Şablonu Güncellendi",
      SUREC_SABLONU_SILINDI: "Süreç Şablonu Silindi",
      SUREC_GOREVI_EKLENDI: "Süreç Görevi Eklendi",
      SUREC_GOREVI_SILINDI: "Süreç Görevi Silindi",
      SUREC_GOREVI_ARSIVLENDI: "Süreç Görevi Arşivlendi",
      SUREC_GOREVLERI_TOPLU_EKLENDI: "Süreç Görevleri Toplu Eklendi",
      SUREC_GOREVLERI_TOPLU_ARSIVLENDI: "Süreç Görevleri Toplu Arşivlendi",
      SUREC_ADIMI_TAMAMLANDI: "Süreç Adımı Tamamlandı",
      SUREC_ADIMLARI_GUNCELLENDI: "Süreç Adımları Güncellendi",
      SUREC_SORUMLUSU_EKLENDI: "Süreç Sorumlusu Eklendi",
      SUREC_SORUMLUSU_GUNCELLENDI: "Süreç Sorumlusu Güncellendi",
      SUREC_SORUMLUSU_SILINDI: "Süreç Sorumlusu Silindi",
      SUREC_SORUMLU_SIRASI_GUNCELLENDI: "Süreç Sorumlu Sırası Güncellendi",
      SUREC_GOREV_SORUMLUSU_DEGISTI: "Süreç Görev Sorumlusu Değişti",
      SUREC_NOTU_EKLENDI: "Süreç Notu Eklendi",
      SUREC_NOTU_SILINDI: "Süreç Notu Silindi",
      SUREC_TEKRAR_AYARI_GUNCELLENDI: "Süreç Tekrar Ayarı Güncellendi",
      SUREC_ZAMAN_TAKIBI_BASLATILDI: "Süreç Zaman Takibi Başlatıldı",
      SUREC_ZAMAN_TAKIBI_DURDURULDU: "Süreç Zaman Takibi Durduruldu",
      SUREC_MANUEL_SURE_EKLENDI: "Süreç Manuel Süre Eklendi",
      SUREC_HATIRLATMA_TASLAGI_OLUSTURULDU: "Süreç Hatırlatma Taslağı Oluşturuldu",
      SUREC_GUN_SAYISI_RAPOR_DURUMU_GUNCELLENDI: "Gün Sayısı Rapor Durumu Güncellendi"
    };

    return labels[action] || String(action || "-").replaceAll("_", " ");
  };

  const [searchText, setSearchText] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [userFilter, setUserFilter] = useState("all");

    const userFilterOptions = useMemo(() => {
      const userMap = new Map();

      activityLogs.forEach((log) => {
        const email = String(log.userEmail || "").trim();
        if (!email) return;

        userMap.set(email.toLowerCase(), email);
      });

      return Array.from(userMap.values()).sort((firstUser, secondUser) =>
        firstUser.localeCompare(secondUser, "tr")
      );
    }, [activityLogs]);

    const filteredLogs = activityLogs.filter((log) => {
      const text = searchText.toLowerCase();
      const logUserEmail = String(log.userEmail || "").toLowerCase();

      const matchesSearch =
        logUserEmail.includes(text) ||
        log.action?.toLowerCase().includes(text) ||
        log.detail?.toLowerCase().includes(text) ||
        getActionLabel(log.action)?.toLowerCase().includes(text);

      if (!matchesSearch) return false;

      if (userFilter !== "all" && logUserEmail !== userFilter) return false;

      if (dateFilter === "all") return true;

      if (!log.createdAt?.toDate) return false;

      const logDate = log.createdAt.toDate();
      const now = new Date();

      const diffDays =
        (now.getTime() - logDate.getTime()) / (1000 * 60 * 60 * 24);

      if (dateFilter === "today") {
        return logDate.toDateString() === now.toDateString();
      }

      if (dateFilter === "7days") {
        return diffDays <= 7;
      }

      if (dateFilter === "30days") {
        return diffDays <= 30;
      }

      return true;
    });

    const exportLogsToExcel = () => {
      const rows = filteredLogs.map((log) => ({
        Tarih: log.createdAt?.toDate
          ? log.createdAt.toDate().toLocaleString("tr-TR")
          : "-",
        Kullanıcı: log.userEmail || "-",
        İşlem: getActionLabel(log.action),
        Detay: log.detail || "-"
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(workbook, worksheet, "Sistem Hareketleri");
      XLSX.writeFile(workbook, "sistem-hareketleri.xlsx");
    };

  const getActionStyle = (action) => {
    if (action?.includes("SILINDI")) {
      return {
        background: isDark ? "#3b1418" : "#fee2e2",
        color: isDark ? "#fca5a5" : "#b91c1c"
      };
    }

    if (action?.includes("YUKLENDI") || action?.includes("EKLENDI")) {
      return {
        background: isDark ? "#12351f" : "#dcfce7",
        color: isDark ? "#86efac" : "#166534"
      };
    }

    if (action?.includes("GUNCELLENDI")) {
      return {
        background: isDark ? "#102a4a" : "#dbeafe",
        color: isDark ? "#93c5fd" : "#1d4ed8"
      };
    }

    if (action?.includes("KULLANICI")) {
      return {
        background: isDark ? "#2d1d44" : "#f3e8ff",
        color: isDark ? "#d8b4fe" : "#7e22ce"
      };
    }

    return {
      background: isDark ? "#253247" : "#f3f4f6",
      color: isDark ? "#dbeafe" : "#374151"
    };
  };

  const formatDate = (createdAt) => {
    if (!createdAt?.toDate) return "-";

    return createdAt.toDate().toLocaleString("tr-TR");
  };

  const latestLog = activityLogs[0];
  const summaryCards = useMemo(() => ([
    { label: "Toplam Hareket", value: activityLogs.length },
    { label: "Son İşlem", value: latestLog ? getActionLabel(latestLog.action) : "-" },
    { label: "Son İşlem Tarihi", value: latestLog ? formatDate(latestLog.createdAt) : "-" }
  ]), [activityLogs.length, latestLog]);

  return (
    <div style={{ color: palette.pageText }}>
      <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 14,
            flexWrap: "wrap",
            marginBottom: 20
          }}
        >
          <div>
            <h2 style={{ margin: 0 }}>Sistem Hareketleri</h2>
            <div style={{ color: palette.muted, fontWeight: 700, fontSize: 13, marginTop: 6 }}>
              Kullanıcıların panelde yaptığı işlemler ve süreç hareketleri
            </div>
          </div>

          <button
            type="button"
            onClick={exportLogsToExcel}
            style={{
              border: "none",
              background: "#124254",
              color: "#fff",
              padding: "10px 14px",
              borderRadius: 12,
              fontWeight: 800,
              cursor: "pointer"
            }}
          >
            Excel&apos;e Aktar
          </button>
        </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 16,
          marginBottom: 20
        }}
      >
        {summaryCards.map((card) => (
          <div key={card.label} style={summaryCardStyle(palette)}>
            <div style={summaryLabelStyle(palette)}>{card.label}</div>
            <div style={{ ...summaryValueStyle(palette), fontSize: card.label === "Toplam Hareket" ? 28 : 16 }}>
              {card.value}
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
        <input
          type="text"
          placeholder="Kullanıcı, işlem veya detay ara..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{
            flex: "1 1 360px",
            padding: "12px 14px",
            borderRadius: 12,
            border: `1px solid ${palette.borderSoft}`,
            background: palette.input,
            color: palette.pageText,
            fontSize: 14,
            outline: "none"
          }}
        />

        <select
          value={userFilter}
          onChange={(e) => setUserFilter(e.target.value)}
          style={{
            width: 260,
            padding: "12px 14px",
            borderRadius: 12,
            border: `1px solid ${palette.borderSoft}`,
            background: palette.input,
            color: palette.pageText,
            fontSize: 14
          }}
        >
          <option value="all">Tüm Kullanıcılar</option>
          {userFilterOptions.map((userEmail) => (
            <option key={userEmail} value={userEmail.toLowerCase()}>
              {userEmail}
            </option>
          ))}
        </select>

        <select
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          style={{
            width: 220,
            padding: "12px 14px",
            borderRadius: 12,
            border: `1px solid ${palette.borderSoft}`,
            background: palette.input,
            color: palette.pageText,
            fontSize: 14
          }}
        >
          <option value="all">Tüm Tarihler</option>
          <option value="today">Bugün</option>
          <option value="7days">Son 7 Gün</option>
          <option value="30days">Son 30 Gün</option>
        </select>
      </div>

      <div
        style={{
          background: palette.card,
          borderRadius: 18,
          padding: 18,
          border: `1px solid ${palette.border}`,
          boxShadow: isDark ? "none" : "0 8px 24px rgba(0,0,0,0.06)"
        }}
      >
        <div style={{ display: "grid", gap: 10 }}>
          {filteredLogs.map((log) => (
            <div
              key={log.id}
              style={{
                padding: "14px 16px",
                borderRadius: 16,
                border: `1px solid ${palette.border}`,
                display: "grid",
                gridTemplateColumns: "170px minmax(180px, 240px) minmax(180px, 260px) 1fr",
                gap: 12,
                alignItems: "start",
                background: palette.card2
              }}
            >
              <div>
                <div style={fieldLabelStyle(palette)}>Tarih</div>
                <div style={{ fontSize: 13, color: palette.muted, fontWeight: 800 }}>
                  {formatDate(log.createdAt)}
                </div>
              </div>

              <div style={{ minWidth: 0 }}>
                <div style={fieldLabelStyle(palette)}>Kullanıcı</div>
                <div
                  title={log.userEmail || "-"}
                  style={{
                    fontSize: 13,
                    color: palette.pageText,
                    fontWeight: 850,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                  }}
                >
                  {log.userEmail || "-"}
                </div>
              </div>

              <div>
                <div style={fieldLabelStyle(palette)}>İşlem</div>
                <span
                  style={{
                    display: "inline-flex",
                    maxWidth: "100%",
                    padding: "6px 10px",
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 900,
                    lineHeight: 1.2,
                    whiteSpace: "normal",
                    ...getActionStyle(log.action)
                  }}
                >
                  {getActionLabel(log.action)}
                </span>
              </div>

              <div style={{ minWidth: 0 }}>
                <div style={fieldLabelStyle(palette)}>Detay</div>
                <div style={{ fontSize: 13, color: palette.detail, fontWeight: 650, lineHeight: 1.45, overflowWrap: "anywhere" }}>
                  {log.detail || "-"}
                </div>
              </div>
            </div>
          ))}

          {filteredLogs.length === 0 && (
            <div
              style={{
                padding: 24,
                textAlign: "center",
                color: palette.muted,
                background: palette.card2,
                border: `1px dashed ${palette.borderSoft}`,
                borderRadius: 14,
                fontWeight: 600
              }}
            >
              Henüz sistem hareketi bulunmuyor.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const summaryCardStyle = (palette) => ({
  background: palette.card,
  padding: 20,
  borderRadius: 18,
  border: `1px solid ${palette.border}`,
  boxShadow: palette.card === "#fff" ? "0 8px 24px rgba(0,0,0,0.06)" : "none"
});

const summaryLabelStyle = (palette) => ({
  fontSize: 13,
  color: palette.muted,
  fontWeight: 700
});

const summaryValueStyle = (palette) => ({
  fontSize: 28,
  fontWeight: 900,
  color: palette.primary,
  marginTop: 8
});

const fieldLabelStyle = (palette) => ({
  color: palette.muted,
  fontSize: 10,
  fontWeight: 900,
  letterSpacing: 0.4,
  textTransform: "uppercase",
  marginBottom: 5
});

export default LogsView;
